import { Component, inject, signal, computed } from '@angular/core';
import { PATH_RELOAD_PAGES, PATH_RELOAD } from 'src/app/global';
import { ContainerId } from '../../../models/drawer';
import { Router } from '@angular/router';
import { MedicationService } from 'src/app/calvin-emr-shared/services/medication.service';
import { PageComponent } from 'src/app/calvin-emr-shared/page/page.component';
import { ContainersService } from 'src/app/knightmeds/services/containers.service';
import { Container } from 'src/app/knightmeds/models/container.model';
import { ProgressBar } from 'primeng/progressbar';
import { KnightmedsDiagramComponent } from 'src/app/knightmeds/knightmeds-diagram/knightmeds-diagram.component';
/** A page used to select a container to reload */
@Component({
  selector: 'app-containers-page',
  templateUrl: './containers-page.component.html',
  styleUrls: ['./containers-page.component.scss'],
  standalone: true,
  imports: [
    ProgressBar,
    KnightmedsDiagramComponent,
  ],
})
export class ContainersPageComponent extends PageComponent {
  /** A list of containers indexed by container ids */
  private containersSignal = signal<Container[]>([]);
  readonly containers = computed(() => this.containersSignal());

  /** A list of labels for the containers indexed by container ids */
  private containerLabelsSignal = signal<string[]>([]);
  readonly containerLabels = computed(() => this.containerLabelsSignal());

  /** A list of warnings (out of stock) for the containers indexed by container ids */
  private containerWarningsSignal = signal<string[]>([]);
  readonly containerWarnings = computed(() => this.containerWarningsSignal());

  /** Whether all necessary data has loaded */
  private loadedSignal = signal(false);
  readonly loaded = computed(() => this.loadedSignal());

  private router = inject(Router);
  private medicationService = inject(MedicationService);
  private containersService = inject(ContainersService);

  constructor() {
    super();
  }

  override ngOnInit(): void {
    super.ngOnInit();

    this.containersService.getAllContainers().subscribe(containers => {
      this.containersSignal.set(containers);

      // Get the container labels based off of the medications in each container.
      // Indexed by container id (not array position) -- the diagram reads
      // containerLabels()![id], and containers isn't guaranteed to be a
      // gap-free 0..N-1 run (a drawer with no Firestore doc yet just isn't
      // in this array at all), so array position doesn't line up with id.
      const labels: string[] = [];
      let completed = 0;

      containers.forEach(container => {
        if (container.medicationId == undefined) {
          labels[container.id] = "Empty";
          completed++;
        } else {
          this.medicationService.getMedication(container.medicationId)
            .subscribe(snapshot => {
              labels[container.id] = MedicationService.getMedicationFromSnapshot(snapshot).getName();
              completed++;
              if (completed === containers.length) {
                this.containerLabelsSignal.set(labels);
              }
            });
        }
      });

      // Warn if a container's medication quantity is 0 -- also indexed by
      // container id, for the same reason as the labels above.
      const warnings: string[] = [];
      containers.forEach(container => {
        warnings[container.id] = container.quantity === 0 ? "Out Of Stock" : "";
      });
      this.containerWarningsSignal.set(warnings);

      this.loadedSignal.set(true);
    });
  }

  /** Navigate to a container's reload page */
  onContainerClicked(containerId: ContainerId): void {
    this.router.navigate(["/", PATH_RELOAD_PAGES, PATH_RELOAD, containerId.toString()]);
  }

}
